import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { ArrowUpRight, ArrowDownRight, Minus, RefreshCw } from "lucide-react";

/**
 * YoY (Year-on-Year) snapshot card for Schedule III financial reports.
 *
 * Props:
 *   endpoint   — base path like "/companies/{cid}/reports/profit-loss"
 *                We will append "/compare" and pass the same query params + prior_start/prior_end.
 *   params     — current period query params: {start, end} (PL/CF) or {end} (BS)
 *   metrics    — array of { label, path, format? } describing which numbers to show.
 *                path is a dotted lookup against the response's `current` / `prior`
 *                objects. Variance is auto-computed from the response's `variance` map
 *                if the metric name matches; otherwise computed inline.
 *
 * Renders:
 *   Header strip with "Year-on-Year comparison" + period labels.
 *   A grid of metric cards: current value, prior value, absolute change, % change.
 */
function get(obj, dotted) {
  return dotted.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}

function fmtINR(v) {
  if (v === 0 || v == null) return "—";
  if (v < 0) return `(₹${formatINR(Math.abs(v))})`;
  return `₹${formatINR(v)}`;
}

function VarianceChip({ pct, abs }) {
  if (abs === 0 || abs == null) return <span className="text-xs text-gray-400"><Minus className="w-3 h-3 inline" /> No change</span>;
  const up = abs > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const color = up ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-700 bg-red-50 border-red-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-1.5 py-0.5 ${color}`}>
      <Icon className="w-3 h-3" />
      {pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`}
    </span>
  );
}

export default function YoYSnapshot({ endpoint, params, metrics, testid = "yoy-snapshot" }) {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Reset data + loading flag when toggling off so a re-toggle re-fetches.
  const toggleOn = () => { setData(null); setLoading(true); setEnabled(true); };
  const toggleOff = () => { setEnabled(false); setData(null); };

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api.get(`${endpoint}/compare`, { params }).then((r) => {
      if (cancelled) return;
      setData(r.data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [enabled, endpoint, params]);

  if (!enabled) {
    return (
      <div className="border border-gray-200 rounded bg-white" data-testid={testid}>
        <button
          onClick={toggleOn}
          className="w-full text-left px-4 py-3 text-xs font-semibold text-blue-700 hover:bg-blue-50 flex items-center gap-2"
          data-testid={`${testid}-toggle`}
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Compare with previous year (YoY)
          <span className="ml-auto text-[10px] text-gray-500 font-normal">Click to load prior-period figures</span>
        </button>
      </div>
    );
  }

  return (
    <div className="border border-blue-200 rounded bg-white overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border-b border-blue-200">
        <div>
          <div className="text-xs font-semibold text-blue-900">Year-on-Year Comparison</div>
          <div className="text-[10px] text-blue-700">
            {data ? <>Current: <span className="font-mono">{data.current_label}</span> · Prior: <span className="font-mono">{data.prior_label}</span></> : "Loading…"}
          </div>
        </div>
        <button onClick={toggleOff} className="text-[10px] text-blue-700 hover:underline" data-testid={`${testid}-hide`}>Hide</button>
      </div>
      {loading && <div className="px-4 py-6 text-xs text-gray-500" data-testid={`${testid}-loading`}>Loading prior-period data…</div>}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 p-3">
          {metrics.map((m) => {
            const cur = get(data.current, m.path) ?? 0;
            const pri = get(data.prior, m.path) ?? 0;
            const variance = data.variance && data.variance[m.varKey] ? data.variance[m.varKey] : { absolute: cur - pri, percent: pri ? ((cur - pri) / Math.abs(pri)) * 100 : (cur ? 100 : null) };
            return (
              <div key={m.label} className="border border-gray-200 rounded p-3" data-testid={`${testid}-metric-${m.varKey}`}>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{m.label}</div>
                <div className="text-base font-semibold text-gray-900 font-mono tabular-nums" data-testid={`${testid}-current-${m.varKey}`}>{fmtINR(cur)}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Prior: <span className="font-mono">{fmtINR(pri)}</span></div>
                <div className="mt-1.5">
                  <VarianceChip pct={variance.percent} abs={variance.absolute} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
